/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

/*
* The following modules are intentionally not exported: function-object, napi, unary
*/

// MODULES //

import setReadOnly from '@stdlib/utils-define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name any
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/any}
*/
import any from '@stdlib/ndarray-base-any';
setReadOnly( ns, 'any', any );

/**
* @name anyBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/any-by}
*/
import anyBy from '@stdlib/ndarray-base-any-by';
setReadOnly( ns, 'anyBy', anyBy );

/**
* @name appendSingletonDimensions
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/append-singleton-dimensions}
*/
import appendSingletonDimensions from '@stdlib/ndarray-base-append-singleton-dimensions';
setReadOnly( ns, 'appendSingletonDimensions', appendSingletonDimensions );

/**
* @name assert
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/ndarray/base/assert}
*/
import assert from '@stdlib/ndarray-base-assert';
setReadOnly( ns, 'assert', assert );

/**
* @name assign
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assign}
*/
import assign from '@stdlib/ndarray-base-assign';
setReadOnly( ns, 'assign', assign );

/**
* @name assignDiagonal
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assign-diagonal}
*/
import assignDiagonal from '@stdlib/ndarray-base-assign-diagonal';
setReadOnly( ns, 'assignDiagonal', assignDiagonal );

/**
* @name assignScalar
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assign-scalar}
*/
import assignScalar from '@stdlib/ndarray-base-assign-scalar';
setReadOnly( ns, 'assignScalar', assignScalar );

/**
* @name atleast1d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/atleast1d}
*/
import atleast1d from '@stdlib/ndarray-base-atleast1d';
setReadOnly( ns, 'atleast1d', atleast1d );

/**
* @name atleast2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/atleast2d}
*/
import atleast2d from '@stdlib/ndarray-base-atleast2d';
setReadOnly( ns, 'atleast2d', atleast2d );

/**
* @name atleast3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/atleast3d}
*/
import atleast3d from '@stdlib/ndarray-base-atleast3d';
setReadOnly( ns, 'atleast3d', atleast3d );

/**
* @name atleastnd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/atleastnd}
*/
import atleastnd from '@stdlib/ndarray-base-atleastnd';
setReadOnly( ns, 'atleastnd', atleastnd );

/**
* @name binary
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/binary}
*/
import binary from '@stdlib/ndarray-base-binary';
setReadOnly( ns, 'binary', binary );

/**
* @name binaryInputCastingDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/binary-input-casting-dtype}
*/
import binaryInputCastingDataType from '@stdlib/ndarray-base-binary-input-casting-dtype';
setReadOnly( ns, 'binaryInputCastingDataType', binaryInputCastingDataType );

/**
* @name binaryLoopOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/binary-loop-interchange-order}
*/
import binaryLoopOrder from '@stdlib/ndarray-base-binary-loop-interchange-order';
setReadOnly( ns, 'binaryLoopOrder', binaryLoopOrder );

/**
* @name binaryOutputDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/binary-output-dtype}
*/
import binaryOutputDataType from '@stdlib/ndarray-base-binary-output-dtype';
setReadOnly( ns, 'binaryOutputDataType', binaryOutputDataType );

/**
* @name binaryReduceStrided1d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/binary-reduce-strided1d}
*/
import binaryReduceStrided1d from '@stdlib/ndarray-base-binary-reduce-strided1d';
setReadOnly( ns, 'binaryReduceStrided1d', binaryReduceStrided1d );

/**
* @name binaryReduceStrided1dDispatch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/binary-reduce-strided1d-dispatch}
*/
import binaryReduceStrided1dDispatch from '@stdlib/ndarray-base-binary-reduce-strided1d-dispatch';
setReadOnly( ns, 'binaryReduceStrided1dDispatch', binaryReduceStrided1dDispatch );

/**
* @name binaryReduceStrided1dDispatchFactory
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/binary-reduce-strided1d-dispatch-factory}
*/
import binaryReduceStrided1dDispatchFactory from '@stdlib/ndarray-base-binary-reduce-strided1d-dispatch-factory';
setReadOnly( ns, 'binaryReduceStrided1dDispatchFactory', binaryReduceStrided1dDispatchFactory );

/**
* @name binaryBlockSize
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/binary-tiling-block-size}
*/
import binaryBlockSize from '@stdlib/ndarray-base-binary-tiling-block-size';
setReadOnly( ns, 'binaryBlockSize', binaryBlockSize );

/**
* @name bind2vind
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/bind2vind}
*/
import bind2vind from '@stdlib/ndarray-base-bind2vind';
setReadOnly( ns, 'bind2vind', bind2vind );

/**
* @name broadcastArray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/broadcast-array}
*/
import broadcastArray from '@stdlib/ndarray-base-broadcast-array';
setReadOnly( ns, 'broadcastArray', broadcastArray );

/**
* @name broadcastArrayExceptDimensions
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/broadcast-array-except-dimensions}
*/
import broadcastArrayExceptDimensions from '@stdlib/ndarray-base-broadcast-array-except-dimensions';
setReadOnly( ns, 'broadcastArrayExceptDimensions', broadcastArrayExceptDimensions );

/**
* @name broadcastArrays
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/broadcast-arrays}
*/
import broadcastArrays from '@stdlib/ndarray-base-broadcast-arrays';
setReadOnly( ns, 'broadcastArrays', broadcastArrays );

/**
* @name broadcastScalar
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/broadcast-scalar}
*/
import broadcastScalar from '@stdlib/ndarray-base-broadcast-scalar';
setReadOnly( ns, 'broadcastScalar', broadcastScalar );

/**
* @name broadcastScalarLike
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/broadcast-scalar-like}
*/
import broadcastScalarLike from '@stdlib/ndarray-base-broadcast-scalar-like';
setReadOnly( ns, 'broadcastScalarLike', broadcastScalarLike );

/**
* @name broadcastShapes
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/broadcast-shapes}
*/
import broadcastShapes from '@stdlib/ndarray-base-broadcast-shapes';
setReadOnly( ns, 'broadcastShapes', broadcastShapes );

/**
* @name buffer
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/buffer}
*/
import buffer from '@stdlib/ndarray-base-buffer';
setReadOnly( ns, 'buffer', buffer );

/**
* @name bufferCtors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/buffer-ctors}
*/
import bufferCtors from '@stdlib/ndarray-base-buffer-ctors';
setReadOnly( ns, 'bufferCtors', bufferCtors );

/**
* @name bufferDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/buffer-dtype}
*/
import bufferDataType from '@stdlib/ndarray-base-buffer-dtype';
setReadOnly( ns, 'bufferDataType', bufferDataType );

/**
* @name bufferDataTypeEnum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/buffer-dtype-enum}
*/
import bufferDataTypeEnum from '@stdlib/ndarray-base-buffer-dtype-enum';
setReadOnly( ns, 'bufferDataTypeEnum', bufferDataTypeEnum );

/**
* @name bytesPerElement
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/bytes-per-element}
*/
import bytesPerElement from '@stdlib/ndarray-base-bytes-per-element';
setReadOnly( ns, 'bytesPerElement', bytesPerElement );

/**
* @name char2dtype
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/char2dtype}
*/
import char2dtype from '@stdlib/ndarray-base-char2dtype';
setReadOnly( ns, 'char2dtype', char2dtype );

/**
* @name clampIndex
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/clamp-index}
*/
import clampIndex from '@stdlib/ndarray-base-clamp-index';
setReadOnly( ns, 'clampIndex', clampIndex );

/**
* @name clipIndex
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/clip-index}
*/
import clipIndex from '@stdlib/ndarray-base-clip-index';
setReadOnly( ns, 'clipIndex', clipIndex );

/**
* @name complementShape
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/complement-shape}
*/
import complementShape from '@stdlib/ndarray-base-complement-shape';
setReadOnly( ns, 'complementShape', complementShape );

/**
* @name consensusOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/consensus-order}
*/
import consensusOrder from '@stdlib/ndarray-base-consensus-order';
setReadOnly( ns, 'consensusOrder', consensusOrder );

/**
* @name copy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/copy}
*/
import copy from '@stdlib/ndarray-base-copy';
setReadOnly( ns, 'copy', copy );

/**
* @name countFalsy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/count-falsy}
*/
import countFalsy from '@stdlib/ndarray-base-count-falsy';
setReadOnly( ns, 'countFalsy', countFalsy );

/**
* @name countIf
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/count-if}
*/
import countIf from '@stdlib/ndarray-base-count-if';
setReadOnly( ns, 'countIf', countIf );

/**
* @name countTruthy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/count-truthy}
*/
import countTruthy from '@stdlib/ndarray-base-count-truthy';
setReadOnly( ns, 'countTruthy', countTruthy );

/**
* @name ndarray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ctor}
*/
import ndarray from '@stdlib/ndarray-base-ctor';
setReadOnly( ns, 'ndarray', ndarray );

/**
* @name data
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/data-buffer}
*/
import data from '@stdlib/ndarray-base-data-buffer';
setReadOnly( ns, 'data', data );

/**
* @name descriptor
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/descriptor}
*/
import descriptor from '@stdlib/ndarray-base-descriptor';
setReadOnly( ns, 'descriptor', descriptor );

/**
* @name diagonal
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/diagonal}
*/
import diagonal from '@stdlib/ndarray-base-diagonal';
setReadOnly( ns, 'diagonal', diagonal );

/**
* @name dtype
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype}
*/
import dtype from '@stdlib/ndarray-base-dtype';
setReadOnly( ns, 'dtype', dtype );

/**
* @name dtypeAlignment
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype-alignment}
*/
import dtypeAlignment from '@stdlib/ndarray-base-dtype-alignment';
setReadOnly( ns, 'dtypeAlignment', dtypeAlignment );

/**
* @name dtypeChar
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype-char}
*/
import dtypeChar from '@stdlib/ndarray-base-dtype-char';
setReadOnly( ns, 'dtypeChar', dtypeChar );

/**
* @name dtypeChars
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype-chars}
*/
import dtypeChars from '@stdlib/ndarray-base-dtype-chars';
setReadOnly( ns, 'dtypeChars', dtypeChars );

/**
* @name dtypeDesc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype-desc}
*/
import dtypeDesc from '@stdlib/ndarray-base-dtype-desc';
setReadOnly( ns, 'dtypeDesc', dtypeDesc );

/**
* @name dtypeEnum2Str
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype-enum2str}
*/
import dtypeEnum2Str from '@stdlib/ndarray-base-dtype-enum2str';
setReadOnly( ns, 'dtypeEnum2Str', dtypeEnum2Str );

/**
* @name dtypeEnums
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype-enums}
*/
import dtypeEnums from '@stdlib/ndarray-base-dtype-enums';
setReadOnly( ns, 'dtypeEnums', dtypeEnums );

/**
* @name dtypeObjects
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype-objects}
*/
import dtypeObjects from '@stdlib/ndarray-base-dtype-objects';
setReadOnly( ns, 'dtypeObjects', dtypeObjects );

/**
* @name dtypeResolveEnum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype-resolve-enum}
*/
import dtypeResolveEnum from '@stdlib/ndarray-base-dtype-resolve-enum';
setReadOnly( ns, 'dtypeResolveEnum', dtypeResolveEnum );

/**
* @name dtypeResolveStr
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype-resolve-str}
*/
import dtypeResolveStr from '@stdlib/ndarray-base-dtype-resolve-str';
setReadOnly( ns, 'dtypeResolveStr', dtypeResolveStr );

/**
* @name dtypeStr2Enum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype-str2enum}
*/
import dtypeStr2Enum from '@stdlib/ndarray-base-dtype-str2enum';
setReadOnly( ns, 'dtypeStr2Enum', dtypeStr2Enum );

/**
* @name dtypeStrings
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype-strings}
*/
import dtypeStrings from '@stdlib/ndarray-base-dtype-strings';
setReadOnly( ns, 'dtypeStrings', dtypeStrings );

/**
* @name dtype2c
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtype2c}
*/
import dtype2c from '@stdlib/ndarray-base-dtype2c';
setReadOnly( ns, 'dtype2c', dtype2c );

/**
* @name dtypes2enums
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtypes2enums}
*/
import dtypes2enums from '@stdlib/ndarray-base-dtypes2enums';
setReadOnly( ns, 'dtypes2enums', dtypes2enums );

/**
* @name dtypes2signatures
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtypes2signatures}
*/
import dtypes2signatures from '@stdlib/ndarray-base-dtypes2signatures';
setReadOnly( ns, 'dtypes2signatures', dtypes2signatures );

/**
* @name dtypes2strings
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/dtypes2strings}
*/
import dtypes2strings from '@stdlib/ndarray-base-dtypes2strings';
setReadOnly( ns, 'dtypes2strings', dtypes2strings );

/**
* @name empty
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/empty}
*/
import empty from '@stdlib/ndarray-base-empty';
setReadOnly( ns, 'empty', empty );

/**
* @name emptyLike
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/empty-like}
*/
import emptyLike from '@stdlib/ndarray-base-empty-like';
setReadOnly( ns, 'emptyLike', emptyLike );

/**
* @name every
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/every}
*/
import every from '@stdlib/ndarray-base-every';
setReadOnly( ns, 'every', every );

/**
* @name everyBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/every-by}
*/
import everyBy from '@stdlib/ndarray-base-every-by';
setReadOnly( ns, 'everyBy', everyBy );

/**
* @name expandDimensions
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/expand-dimensions}
*/
import expandDimensions from '@stdlib/ndarray-base-expand-dimensions';
setReadOnly( ns, 'expandDimensions', expandDimensions );

/**
* @name falses
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/falses}
*/
import falses from '@stdlib/ndarray-base-falses';
setReadOnly( ns, 'falses', falses );

/**
* @name falsesLike
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/falses-like}
*/
import falsesLike from '@stdlib/ndarray-base-falses-like';
setReadOnly( ns, 'falsesLike', falsesLike );

/**
* @name fill
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/fill}
*/
import fill from '@stdlib/ndarray-base-fill';
setReadOnly( ns, 'fill', fill );

/**
* @name fillBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/fill-by}
*/
import fillBy from '@stdlib/ndarray-base-fill-by';
setReadOnly( ns, 'fillBy', fillBy );

/**
* @name fillDiagonal
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/fill-diagonal}
*/
import fillDiagonal from '@stdlib/ndarray-base-fill-diagonal';
setReadOnly( ns, 'fillDiagonal', fillDiagonal );

/**
* @name find
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/find}
*/
import find from '@stdlib/ndarray-base-find';
setReadOnly( ns, 'find', find );

/**
* @name flag
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/flag}
*/
import flag from '@stdlib/ndarray-base-flag';
setReadOnly( ns, 'flag', flag );

/**
* @name flags
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/flags}
*/
import flags from '@stdlib/ndarray-base-flags';
setReadOnly( ns, 'flags', flags );

/**
* @name flattenShape
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/flatten-shape}
*/
import flattenShape from '@stdlib/ndarray-base-flatten-shape';
setReadOnly( ns, 'flattenShape', flattenShape );

/**
* @name flattenShapeFrom
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/flatten-shape-from}
*/
import flattenShapeFrom from '@stdlib/ndarray-base-flatten-shape-from';
setReadOnly( ns, 'flattenShapeFrom', flattenShapeFrom );

/**
* @name fliplr
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/fliplr}
*/
import fliplr from '@stdlib/ndarray-base-fliplr';
setReadOnly( ns, 'fliplr', fliplr );

/**
* @name flipud
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/flipud}
*/
import flipud from '@stdlib/ndarray-base-flipud';
setReadOnly( ns, 'flipud', flipud );

/**
* @name forEach
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/for-each}
*/
import forEach from '@stdlib/ndarray-base-for-each';
setReadOnly( ns, 'forEach', forEach );

/**
* @name array2ndarray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/from-array}
*/
import array2ndarray from '@stdlib/ndarray-base-from-array';
setReadOnly( ns, 'array2ndarray', array2ndarray );

/**
* @name scalar2ndarray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/from-scalar}
*/
import scalar2ndarray from '@stdlib/ndarray-base-from-scalar';
setReadOnly( ns, 'scalar2ndarray', scalar2ndarray );

/**
* @name scalar2ndarrayLike
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/from-scalar-like}
*/
import scalar2ndarrayLike from '@stdlib/ndarray-base-from-scalar-like';
setReadOnly( ns, 'scalar2ndarrayLike', scalar2ndarrayLike );

/**
* @name full
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/full}
*/
import full from '@stdlib/ndarray-base-full';
setReadOnly( ns, 'full', full );

/**
* @name fullBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/full-by}
*/
import fullBy from '@stdlib/ndarray-base-full-by';
setReadOnly( ns, 'fullBy', fullBy );

/**
* @name includes
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/includes}
*/
import includes from '@stdlib/ndarray-base-includes';
setReadOnly( ns, 'includes', includes );

/**
* @name ind
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ind}
*/
import ind from '@stdlib/ndarray-base-ind';
setReadOnly( ns, 'ind', ind );

/**
* @name ind2sub
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ind2sub}
*/
import ind2sub from '@stdlib/ndarray-base-ind2sub';
setReadOnly( ns, 'ind2sub', ind2sub );

/**
* @name iterationOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/iteration-order}
*/
import iterationOrder from '@stdlib/ndarray-base-iteration-order';
setReadOnly( ns, 'iterationOrder', iterationOrder );

/**
* @name loopOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/loop-interchange-order}
*/
import loopOrder from '@stdlib/ndarray-base-loop-interchange-order';
setReadOnly( ns, 'loopOrder', loopOrder );

/**
* @name map
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/map}
*/
import map from '@stdlib/ndarray-base-map';
setReadOnly( ns, 'map', map );

/**
* @name maxViewBufferIndex
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/max-view-buffer-index}
*/
import maxViewBufferIndex from '@stdlib/ndarray-base-max-view-buffer-index';
setReadOnly( ns, 'maxViewBufferIndex', maxViewBufferIndex );

/**
* @name maybeBroadcastArray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/maybe-broadcast-array}
*/
import maybeBroadcastArray from '@stdlib/ndarray-base-maybe-broadcast-array';
setReadOnly( ns, 'maybeBroadcastArray', maybeBroadcastArray );

/**
* @name maybeBroadcastArrayExceptDimensions
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/maybe-broadcast-array-except-dimensions}
*/
import maybeBroadcastArrayExceptDimensions from '@stdlib/ndarray-base-maybe-broadcast-array-except-dimensions';
setReadOnly( ns, 'maybeBroadcastArrayExceptDimensions', maybeBroadcastArrayExceptDimensions );

/**
* @name maybeBroadcastArrays
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/maybe-broadcast-arrays}
*/
import maybeBroadcastArrays from '@stdlib/ndarray-base-maybe-broadcast-arrays';
setReadOnly( ns, 'maybeBroadcastArrays', maybeBroadcastArrays );

/**
* @name metaDataProps
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/meta-data-props}
*/
import metaDataProps from '@stdlib/ndarray-base-meta-data-props';
setReadOnly( ns, 'metaDataProps', metaDataProps );

/**
* @name minSignedIntegerDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/min-signed-integer-dtype}
*/
import minSignedIntegerDataType from '@stdlib/ndarray-base-min-signed-integer-dtype';
setReadOnly( ns, 'minSignedIntegerDataType', minSignedIntegerDataType );

/**
* @name minUnsignedIntegerDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/min-unsigned-integer-dtype}
*/
import minUnsignedIntegerDataType from '@stdlib/ndarray-base-min-unsigned-integer-dtype';
setReadOnly( ns, 'minUnsignedIntegerDataType', minUnsignedIntegerDataType );

/**
* @name minViewBufferIndex
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/min-view-buffer-index}
*/
import minViewBufferIndex from '@stdlib/ndarray-base-min-view-buffer-index';
setReadOnly( ns, 'minViewBufferIndex', minViewBufferIndex );

/**
* @name minmaxViewBufferIndex
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/minmax-view-buffer-index}
*/
import minmaxViewBufferIndex from '@stdlib/ndarray-base-minmax-view-buffer-index';
setReadOnly( ns, 'minmaxViewBufferIndex', minmaxViewBufferIndex );

/**
* @name nans
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/nans}
*/
import nans from '@stdlib/ndarray-base-nans';
setReadOnly( ns, 'nans', nans );

/**
* @name nansLike
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/nans-like}
*/
import nansLike from '@stdlib/ndarray-base-nans-like';
setReadOnly( ns, 'nansLike', nansLike );

/**
* @name ndarraylike2descriptor
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ndarraylike2descriptor}
*/
import ndarraylike2descriptor from '@stdlib/ndarray-base-ndarraylike2descriptor';
setReadOnly( ns, 'ndarraylike2descriptor', ndarraylike2descriptor );

/**
* @name ndarraylike2ndarray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ndarraylike2ndarray}
*/
import ndarraylike2ndarray from '@stdlib/ndarray-base-ndarraylike2ndarray';
setReadOnly( ns, 'ndarraylike2ndarray', ndarraylike2ndarray );

/**
* @name ndarraylike2object
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ndarraylike2object}
*/
import ndarraylike2object from '@stdlib/ndarray-base-ndarraylike2object';
setReadOnly( ns, 'ndarraylike2object', ndarraylike2object );

/**
* @name ndarraylike2scalar
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ndarraylike2scalar}
*/
import ndarraylike2scalar from '@stdlib/ndarray-base-ndarraylike2scalar';
setReadOnly( ns, 'ndarraylike2scalar', ndarraylike2scalar );

/**
* @name ndims
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ndims}
*/
import ndims from '@stdlib/ndarray-base-ndims';
setReadOnly( ns, 'ndims', ndims );

/**
* @name nextCartesianIndex
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/next-cartesian-index}
*/
import nextCartesianIndex from '@stdlib/ndarray-base-next-cartesian-index';
setReadOnly( ns, 'nextCartesianIndex', nextCartesianIndex );

/**
* @name nonsingletonDimensions
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/nonsingleton-dimensions}
*/
import nonsingletonDimensions from '@stdlib/ndarray-base-nonsingleton-dimensions';
setReadOnly( ns, 'nonsingletonDimensions', nonsingletonDimensions );

/**
* @name normalizeIndex
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/normalize-index}
*/
import normalizeIndex from '@stdlib/ndarray-base-normalize-index';
setReadOnly( ns, 'normalizeIndex', normalizeIndex );

/**
* @name normalizeIndices
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/normalize-indices}
*/
import normalizeIndices from '@stdlib/ndarray-base-normalize-indices';
setReadOnly( ns, 'normalizeIndices', normalizeIndices );

/**
* @name nullary
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/nullary}
*/
import nullary from '@stdlib/ndarray-base-nullary';
setReadOnly( ns, 'nullary', nullary );

/**
* @name nullaryLoopOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/nullary-loop-interchange-order}
*/
import nullaryLoopOrder from '@stdlib/ndarray-base-nullary-loop-interchange-order';
setReadOnly( ns, 'nullaryLoopOrder', nullaryLoopOrder );

/**
* @name nullaryStrided1d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/nullary-strided1d}
*/
import nullaryStrided1d from '@stdlib/ndarray-base-nullary-strided1d';
setReadOnly( ns, 'nullaryStrided1d', nullaryStrided1d );

/**
* @name nullaryStrided1dDispatch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/nullary-strided1d-dispatch}
*/
import nullaryStrided1dDispatch from '@stdlib/ndarray-base-nullary-strided1d-dispatch';
setReadOnly( ns, 'nullaryStrided1dDispatch', nullaryStrided1dDispatch );

/**
* @name nullaryStrided1dDispatchFactory
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/nullary-strided1d-dispatch-factory}
*/
import nullaryStrided1dDispatchFactory from '@stdlib/ndarray-base-nullary-strided1d-dispatch-factory';
setReadOnly( ns, 'nullaryStrided1dDispatchFactory', nullaryStrided1dDispatchFactory );

/**
* @name nullaryBlockSize
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/nullary-tiling-block-size}
*/
import nullaryBlockSize from '@stdlib/ndarray-base-nullary-tiling-block-size';
setReadOnly( ns, 'nullaryBlockSize', nullaryBlockSize );

/**
* @name nulls
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/nulls}
*/
import nulls from '@stdlib/ndarray-base-nulls';
setReadOnly( ns, 'nulls', nulls );

/**
* @name nullsLike
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/nulls-like}
*/
import nullsLike from '@stdlib/ndarray-base-nulls-like';
setReadOnly( ns, 'nullsLike', nullsLike );

/**
* @name numel
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/numel}
*/
import numel from '@stdlib/ndarray-base-numel';
setReadOnly( ns, 'numel', numel );

/**
* @name numelDimension
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/numel-dimension}
*/
import numelDimension from '@stdlib/ndarray-base-numel-dimension';
setReadOnly( ns, 'numelDimension', numelDimension );

/**
* @name offset
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/offset}
*/
import offset from '@stdlib/ndarray-base-offset';
setReadOnly( ns, 'offset', offset );

/**
* @name offsets
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/offsets}
*/
import offsets from '@stdlib/ndarray-base-offsets';
setReadOnly( ns, 'offsets', offsets );

/**
* @name ones
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ones}
*/
import ones from '@stdlib/ndarray-base-ones';
setReadOnly( ns, 'ones', ones );

/**
* @name onesLike
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ones-like}
*/
import onesLike from '@stdlib/ndarray-base-ones-like';
setReadOnly( ns, 'onesLike', onesLike );

/**
* @name order
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/order}
*/
import order from '@stdlib/ndarray-base-order';
setReadOnly( ns, 'order', order );

/**
* @name outputDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/output-dtype}
*/
import outputDataType from '@stdlib/ndarray-base-output-dtype';
setReadOnly( ns, 'outputDataType', outputDataType );

/**
* @name outputOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/output-order}
*/
import outputOrder from '@stdlib/ndarray-base-output-order';
setReadOnly( ns, 'outputOrder', outputOrder );

/**
* @name outputPolicyEnum2Str
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/output-policy-enum2str}
*/
import outputPolicyEnum2Str from '@stdlib/ndarray-base-output-policy-enum2str';
setReadOnly( ns, 'outputPolicyEnum2Str', outputPolicyEnum2Str );

/**
* @name outputPolicyResolveEnum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/output-policy-resolve-enum}
*/
import outputPolicyResolveEnum from '@stdlib/ndarray-base-output-policy-resolve-enum';
setReadOnly( ns, 'outputPolicyResolveEnum', outputPolicyResolveEnum );

/**
* @name outputPolicyResolveStr
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/output-policy-resolve-str}
*/
import outputPolicyResolveStr from '@stdlib/ndarray-base-output-policy-resolve-str';
setReadOnly( ns, 'outputPolicyResolveStr', outputPolicyResolveStr );

/**
* @name outputPolicyStr2Enum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/output-policy-str2enum}
*/
import outputPolicyStr2Enum from '@stdlib/ndarray-base-output-policy-str2enum';
setReadOnly( ns, 'outputPolicyStr2Enum', outputPolicyStr2Enum );

/**
* @name pop
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/pop}
*/
import pop from '@stdlib/ndarray-base-pop';
setReadOnly( ns, 'pop', pop );

/**
* @name prependSingletonDimensions
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/prepend-singleton-dimensions}
*/
import prependSingletonDimensions from '@stdlib/ndarray-base-prepend-singleton-dimensions';
setReadOnly( ns, 'prependSingletonDimensions', prependSingletonDimensions );

/**
* @name promoteDataTypes
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/promote-dtypes}
*/
import promoteDataTypes from '@stdlib/ndarray-base-promote-dtypes';
setReadOnly( ns, 'promoteDataTypes', promoteDataTypes );

/**
* @name quaternaryLoopOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/quaternary-loop-interchange-order}
*/
import quaternaryLoopOrder from '@stdlib/ndarray-base-quaternary-loop-interchange-order';
setReadOnly( ns, 'quaternaryLoopOrder', quaternaryLoopOrder );

/**
* @name quaternaryBlockSize
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/quaternary-tiling-block-size}
*/
import quaternaryBlockSize from '@stdlib/ndarray-base-quaternary-tiling-block-size';
setReadOnly( ns, 'quaternaryBlockSize', quaternaryBlockSize );

/**
* @name quinaryLoopOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/quinary-loop-interchange-order}
*/
import quinaryLoopOrder from '@stdlib/ndarray-base-quinary-loop-interchange-order';
setReadOnly( ns, 'quinaryLoopOrder', quinaryLoopOrder );

/**
* @name quinaryBlockSize
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/quinary-tiling-block-size}
*/
import quinaryBlockSize from '@stdlib/ndarray-base-quinary-tiling-block-size';
setReadOnly( ns, 'quinaryBlockSize', quinaryBlockSize );

/**
* @name reinterpretBoolean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/reinterpret-boolean}
*/
import reinterpretBoolean from '@stdlib/ndarray-base-reinterpret-boolean';
setReadOnly( ns, 'reinterpretBoolean', reinterpretBoolean );

/**
* @name reinterpretComplex
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/reinterpret-complex}
*/
import reinterpretComplex from '@stdlib/ndarray-base-reinterpret-complex';
setReadOnly( ns, 'reinterpretComplex', reinterpretComplex );

/**
* @name reinterpretComplex64
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/reinterpret-complex64}
*/
import reinterpretComplex64 from '@stdlib/ndarray-base-reinterpret-complex64';
setReadOnly( ns, 'reinterpretComplex64', reinterpretComplex64 );

/**
* @name reinterpretComplex128
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/reinterpret-complex128}
*/
import reinterpretComplex128 from '@stdlib/ndarray-base-reinterpret-complex128';
setReadOnly( ns, 'reinterpretComplex128', reinterpretComplex128 );

/**
* @name removeSingletonDimensions
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/remove-singleton-dimensions}
*/
import removeSingletonDimensions from '@stdlib/ndarray-base-remove-singleton-dimensions';
setReadOnly( ns, 'removeSingletonDimensions', removeSingletonDimensions );

/**
* @name reverse
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/reverse}
*/
import reverse from '@stdlib/ndarray-base-reverse';
setReadOnly( ns, 'reverse', reverse );

/**
* @name reverseDimension
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/reverse-dimension}
*/
import reverseDimension from '@stdlib/ndarray-base-reverse-dimension';
setReadOnly( ns, 'reverseDimension', reverseDimension );

/**
* @name reverseDimensions
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/reverse-dimensions}
*/
import reverseDimensions from '@stdlib/ndarray-base-reverse-dimensions';
setReadOnly( ns, 'reverseDimensions', reverseDimensions );

/**
* @name rot90
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/rot90}
*/
import rot90 from '@stdlib/ndarray-base-rot90';
setReadOnly( ns, 'rot90', rot90 );

/**
* @name rot180
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/rot180}
*/
import rot180 from '@stdlib/ndarray-base-rot180';
setReadOnly( ns, 'rot180', rot180 );

/**
* @name rotl90
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/rotl90}
*/
import rotl90 from '@stdlib/ndarray-base-rotl90';
setReadOnly( ns, 'rotl90', rotl90 );

/**
* @name rotr90
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/rotr90}
*/
import rotr90 from '@stdlib/ndarray-base-rotr90';
setReadOnly( ns, 'rotr90', rotr90 );

/**
* @name serializeMetaData
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/serialize-meta-data}
*/
import serializeMetaData from '@stdlib/ndarray-base-serialize-meta-data';
setReadOnly( ns, 'serializeMetaData', serializeMetaData );

/**
* @name setDescriptorOffsets
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/set-descriptor-offsets}
*/
import setDescriptorOffsets from '@stdlib/ndarray-base-set-descriptor-offsets';
setReadOnly( ns, 'setDescriptorOffsets', setDescriptorOffsets );

/**
* @name shape
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/shape}
*/
import shape from '@stdlib/ndarray-base-shape';
setReadOnly( ns, 'shape', shape );

/**
* @name shape2strides
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/shape2strides}
*/
import shape2strides from '@stdlib/ndarray-base-shape2strides';
setReadOnly( ns, 'shape2strides', shape2strides );

/**
* @name shift
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/shift}
*/
import shift from '@stdlib/ndarray-base-shift';
setReadOnly( ns, 'shift', shift );

/**
* @name singletonDimensions
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/singleton-dimensions}
*/
import singletonDimensions from '@stdlib/ndarray-base-singleton-dimensions';
setReadOnly( ns, 'singletonDimensions', singletonDimensions );

/**
* @name slice
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/slice}
*/
import slice from '@stdlib/ndarray-base-slice';
setReadOnly( ns, 'slice', slice );

/**
* @name sliceAssign
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/slice-assign}
*/
import sliceAssign from '@stdlib/ndarray-base-slice-assign';
setReadOnly( ns, 'sliceAssign', sliceAssign );

/**
* @name sliceDimension
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/slice-dimension}
*/
import sliceDimension from '@stdlib/ndarray-base-slice-dimension';
setReadOnly( ns, 'sliceDimension', sliceDimension );

/**
* @name sliceDimensionFrom
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/slice-dimension-from}
*/
import sliceDimensionFrom from '@stdlib/ndarray-base-slice-dimension-from';
setReadOnly( ns, 'sliceDimensionFrom', sliceDimensionFrom );

/**
* @name sliceDimensionTo
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/slice-dimension-to}
*/
import sliceDimensionTo from '@stdlib/ndarray-base-slice-dimension-to';
setReadOnly( ns, 'sliceDimensionTo', sliceDimensionTo );

/**
* @name sliceFrom
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/slice-from}
*/
import sliceFrom from '@stdlib/ndarray-base-slice-from';
setReadOnly( ns, 'sliceFrom', sliceFrom );

/**
* @name sliceTo
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/slice-to}
*/
import sliceTo from '@stdlib/ndarray-base-slice-to';
setReadOnly( ns, 'sliceTo', sliceTo );

/**
* @name some
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/some}
*/
import some from '@stdlib/ndarray-base-some';
setReadOnly( ns, 'some', some );

/**
* @name someBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/some-by}
*/
import someBy from '@stdlib/ndarray-base-some-by';
setReadOnly( ns, 'someBy', someBy );

/**
* @name spreadDimensions
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/spread-dimensions}
*/
import spreadDimensions from '@stdlib/ndarray-base-spread-dimensions';
setReadOnly( ns, 'spreadDimensions', spreadDimensions );

/**
* @name stride
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/stride}
*/
import stride from '@stdlib/ndarray-base-stride';
setReadOnly( ns, 'stride', stride );

/**
* @name strides
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/strides}
*/
import strides from '@stdlib/ndarray-base-strides';
setReadOnly( ns, 'strides', strides );

/**
* @name strides2offset
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/strides2offset}
*/
import strides2offset from '@stdlib/ndarray-base-strides2offset';
setReadOnly( ns, 'strides2offset', strides2offset );

/**
* @name strides2order
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/strides2order}
*/
import strides2order from '@stdlib/ndarray-base-strides2order';
setReadOnly( ns, 'strides2order', strides2order );

/**
* @name sub2ind
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/sub2ind}
*/
import sub2ind from '@stdlib/ndarray-base-sub2ind';
setReadOnly( ns, 'sub2ind', sub2ind );

/**
* @name ternary
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ternary}
*/
import ternary from '@stdlib/ndarray-base-ternary';
setReadOnly( ns, 'ternary', ternary );

/**
* @name ternaryLoopOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ternary-loop-interchange-order}
*/
import ternaryLoopOrder from '@stdlib/ndarray-base-ternary-loop-interchange-order';
setReadOnly( ns, 'ternaryLoopOrder', ternaryLoopOrder );

/**
* @name ternaryOutputDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ternary-output-dtype}
*/
import ternaryOutputDataType from '@stdlib/ndarray-base-ternary-output-dtype';
setReadOnly( ns, 'ternaryOutputDataType', ternaryOutputDataType );

/**
* @name ternaryBlockSize
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/ternary-tiling-block-size}
*/
import ternaryBlockSize from '@stdlib/ndarray-base-ternary-tiling-block-size';
setReadOnly( ns, 'ternaryBlockSize', ternaryBlockSize );

/**
* @name tile
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/tile}
*/
import tile from '@stdlib/ndarray-base-tile';
setReadOnly( ns, 'tile', tile );

/**
* @name blockSize
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/tiling-block-size}
*/
import blockSize from '@stdlib/ndarray-base-tiling-block-size';
setReadOnly( ns, 'blockSize', blockSize );

/**
* @name ndarray2array
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-array}
*/
import ndarray2array from '@stdlib/ndarray-base-to-array';
setReadOnly( ns, 'ndarray2array', ndarray2array );

/**
* @name toFlippedlr
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-flippedlr}
*/
import toFlippedlr from '@stdlib/ndarray-base-to-flippedlr';
setReadOnly( ns, 'toFlippedlr', toFlippedlr );

/**
* @name toFlippedud
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-flippedud}
*/
import toFlippedud from '@stdlib/ndarray-base-to-flippedud';
setReadOnly( ns, 'toFlippedud', toFlippedud );

/**
* @name toNormalizedIndices
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-normalized-indices}
*/
import toNormalizedIndices from '@stdlib/ndarray-base-to-normalized-indices';
setReadOnly( ns, 'toNormalizedIndices', toNormalizedIndices );

/**
* @name toReversed
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-reversed}
*/
import toReversed from '@stdlib/ndarray-base-to-reversed';
setReadOnly( ns, 'toReversed', toReversed );

/**
* @name toReversedDimension
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-reversed-dimension}
*/
import toReversedDimension from '@stdlib/ndarray-base-to-reversed-dimension';
setReadOnly( ns, 'toReversedDimension', toReversedDimension );

/**
* @name toReversedDimensions
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-reversed-dimensions}
*/
import toReversedDimensions from '@stdlib/ndarray-base-to-reversed-dimensions';
setReadOnly( ns, 'toReversedDimensions', toReversedDimensions );

/**
* @name toRot90
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-rot90}
*/
import toRot90 from '@stdlib/ndarray-base-to-rot90';
setReadOnly( ns, 'toRot90', toRot90 );

/**
* @name toRot180
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-rot180}
*/
import toRot180 from '@stdlib/ndarray-base-to-rot180';
setReadOnly( ns, 'toRot180', toRot180 );

/**
* @name toRotl90
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-rotl90}
*/
import toRotl90 from '@stdlib/ndarray-base-to-rotl90';
setReadOnly( ns, 'toRotl90', toRotl90 );

/**
* @name toRotr90
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-rotr90}
*/
import toRotr90 from '@stdlib/ndarray-base-to-rotr90';
setReadOnly( ns, 'toRotr90', toRotr90 );

/**
* @name toTransposed
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-transposed}
*/
import toTransposed from '@stdlib/ndarray-base-to-transposed';
setReadOnly( ns, 'toTransposed', toTransposed );

/**
* @name toUnflattened
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-unflattened}
*/
import toUnflattened from '@stdlib/ndarray-base-to-unflattened';
setReadOnly( ns, 'toUnflattened', toUnflattened );

/**
* @name toUniqueNormalizedIndices
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/to-unique-normalized-indices}
*/
import toUniqueNormalizedIndices from '@stdlib/ndarray-base-to-unique-normalized-indices';
setReadOnly( ns, 'toUniqueNormalizedIndices', toUniqueNormalizedIndices );

/**
* @name transpose
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/transpose}
*/
import transpose from '@stdlib/ndarray-base-transpose';
setReadOnly( ns, 'transpose', transpose );

/**
* @name trues
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/trues}
*/
import trues from '@stdlib/ndarray-base-trues';
setReadOnly( ns, 'trues', trues );

/**
* @name truesLike
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/trues-like}
*/
import truesLike from '@stdlib/ndarray-base-trues-like';
setReadOnly( ns, 'truesLike', truesLike );

/**
* @name unary
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary}
*/
import unary from '@stdlib/ndarray-base-unary';
setReadOnly( ns, 'unary', unary );

/**
* @name unaryAccumulate
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-accumulate}
*/
import unaryAccumulate from '@stdlib/ndarray-base-unary-accumulate';
setReadOnly( ns, 'unaryAccumulate', unaryAccumulate );

/**
* @name unaryAddonDispatch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-addon-dispatch}
*/
import unaryAddonDispatch from '@stdlib/ndarray-base-unary-addon-dispatch';
setReadOnly( ns, 'unaryAddonDispatch', unaryAddonDispatch );

/**
* @name unaryBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-by}
*/
import unaryBy from '@stdlib/ndarray-base-unary-by';
setReadOnly( ns, 'unaryBy', unaryBy );

/**
* @name unaryInputCastingDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-input-casting-dtype}
*/
import unaryInputCastingDataType from '@stdlib/ndarray-base-unary-input-casting-dtype';
setReadOnly( ns, 'unaryInputCastingDataType', unaryInputCastingDataType );

/**
* @name unaryLoopOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-loop-interchange-order}
*/
import unaryLoopOrder from '@stdlib/ndarray-base-unary-loop-interchange-order';
setReadOnly( ns, 'unaryLoopOrder', unaryLoopOrder );

/**
* @name unaryOutputDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-output-dtype}
*/
import unaryOutputDataType from '@stdlib/ndarray-base-unary-output-dtype';
setReadOnly( ns, 'unaryOutputDataType', unaryOutputDataType );

/**
* @name unaryReduceStrided1d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-reduce-strided1d}
*/
import unaryReduceStrided1d from '@stdlib/ndarray-base-unary-reduce-strided1d';
setReadOnly( ns, 'unaryReduceStrided1d', unaryReduceStrided1d );

/**
* @name unaryReduceStrided1dAssignStruct
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-reduce-strided1d-assign-struct}
*/
import unaryReduceStrided1dAssignStruct from '@stdlib/ndarray-base-unary-reduce-strided1d-assign-struct';
setReadOnly( ns, 'unaryReduceStrided1dAssignStruct', unaryReduceStrided1dAssignStruct );

/**
* @name unaryReduceStrided1dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-reduce-strided1d-by}
*/
import unaryReduceStrided1dBy from '@stdlib/ndarray-base-unary-reduce-strided1d-by';
setReadOnly( ns, 'unaryReduceStrided1dBy', unaryReduceStrided1dBy );

/**
* @name unaryReduceStrided1dDispatch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-reduce-strided1d-dispatch}
*/
import unaryReduceStrided1dDispatch from '@stdlib/ndarray-base-unary-reduce-strided1d-dispatch';
setReadOnly( ns, 'unaryReduceStrided1dDispatch', unaryReduceStrided1dDispatch );

/**
* @name unaryReduceStrided1dDispatchBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-reduce-strided1d-dispatch-by}
*/
import unaryReduceStrided1dDispatchBy from '@stdlib/ndarray-base-unary-reduce-strided1d-dispatch-by';
setReadOnly( ns, 'unaryReduceStrided1dDispatchBy', unaryReduceStrided1dDispatchBy );

/**
* @name unaryReduceStrided1dDispatchByFactory
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-reduce-strided1d-dispatch-by-factory}
*/
import unaryReduceStrided1dDispatchByFactory from '@stdlib/ndarray-base-unary-reduce-strided1d-dispatch-by-factory';
setReadOnly( ns, 'unaryReduceStrided1dDispatchByFactory', unaryReduceStrided1dDispatchByFactory );

/**
* @name unaryReduceStrided1dDispatchFactory
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-reduce-strided1d-dispatch-factory}
*/
import unaryReduceStrided1dDispatchFactory from '@stdlib/ndarray-base-unary-reduce-strided1d-dispatch-factory';
setReadOnly( ns, 'unaryReduceStrided1dDispatchFactory', unaryReduceStrided1dDispatchFactory );

/**
* @name unaryReduceSubarray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-reduce-subarray}
*/
import unaryReduceSubarray from '@stdlib/ndarray-base-unary-reduce-subarray';
setReadOnly( ns, 'unaryReduceSubarray', unaryReduceSubarray );

/**
* @name unaryReduceSubarrayBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-reduce-subarray-by}
*/
import unaryReduceSubarrayBy from '@stdlib/ndarray-base-unary-reduce-subarray-by';
setReadOnly( ns, 'unaryReduceSubarrayBy', unaryReduceSubarrayBy );

/**
* @name unaryStrided1d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-strided1d}
*/
import unaryStrided1d from '@stdlib/ndarray-base-unary-strided1d';
setReadOnly( ns, 'unaryStrided1d', unaryStrided1d );

/**
* @name unaryStrided1dDispatch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-strided1d-dispatch}
*/
import unaryStrided1dDispatch from '@stdlib/ndarray-base-unary-strided1d-dispatch';
setReadOnly( ns, 'unaryStrided1dDispatch', unaryStrided1dDispatch );

/**
* @name unaryStrided1dDispatchFactory
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-strided1d-dispatch-factory}
*/
import unaryStrided1dDispatchFactory from '@stdlib/ndarray-base-unary-strided1d-dispatch-factory';
setReadOnly( ns, 'unaryStrided1dDispatchFactory', unaryStrided1dDispatchFactory );

/**
* @name unaryBlockSize
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unary-tiling-block-size}
*/
import unaryBlockSize from '@stdlib/ndarray-base-unary-tiling-block-size';
setReadOnly( ns, 'unaryBlockSize', unaryBlockSize );

/**
* @name unflatten
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unflatten}
*/
import unflatten from '@stdlib/ndarray-base-unflatten';
setReadOnly( ns, 'unflatten', unflatten );

/**
* @name unflattenShape
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/unflatten-shape}
*/
import unflattenShape from '@stdlib/ndarray-base-unflatten-shape';
setReadOnly( ns, 'unflattenShape', unflattenShape );

/**
* @name vind2bind
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/vind2bind}
*/
import vind2bind from '@stdlib/ndarray-base-vind2bind';
setReadOnly( ns, 'vind2bind', vind2bind );

/**
* @name wrapIndex
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/wrap-index}
*/
import wrapIndex from '@stdlib/ndarray-base-wrap-index';
setReadOnly( ns, 'wrapIndex', wrapIndex );

/**
* @name zeros
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/zeros}
*/
import zeros from '@stdlib/ndarray-base-zeros';
setReadOnly( ns, 'zeros', zeros );

/**
* @name zerosLike
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/zeros-like}
*/
import zerosLike from '@stdlib/ndarray-base-zeros-like';
setReadOnly( ns, 'zerosLike', zerosLike );

/**
* @name zip2views1d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/zip2views1d}
*/
import zip2views1d from '@stdlib/ndarray-base-zip2views1d';
setReadOnly( ns, 'zip2views1d', zip2views1d );


// EXPORTS //

export default ns;
